import { PortfolioAllocation, PortfolioAllocationDataPoint } from '@models';
import { sortPortfolioDataPoints } from '@products/portfolio/portfolio.utils';
import {
  PortfolioComponentId,
  PortfolioDTO,
  PortfolioTopHoldingsDTO,
} from '@types';
import { formatPercentage } from '@utils/text/number-utils';
import { MapperFactory } from '../mapper-factory';
import { getFirstNonEmptyProp, parseStdNumber, parseYN } from '../mapper.utils';
import { Mapper, MapperParams } from '../type.mapper';

export const enum TopExposureCalc {
  // WARNING: enum values match calcnamestd values from GraphQL service
  SHORT = 'SHORT_EQUITY_AS_PCT_OF_EQUITY',
  LONG = 'LONG_EQUITY_AS_PCT_OF_EQUITY',
  SHORT_POSITION = 'SHORT_TOT_PCT_OF_TOTAL',
  LONG_POSITION = 'LONG_TOT_PCT_OF_TOTAL',
}

// tslint:disable-next-line:max-line-length
// example fund: https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/18120/A/franklin-k-2-alternative-strategies-fund/FAAAX#portfolio
export const isTopLongExposure = (point: PortfolioTopHoldingsDTO): boolean =>
  point.calcnamestd === TopExposureCalc.LONG;

export const isTopShortExposure = (point: PortfolioTopHoldingsDTO): boolean =>
  point.calcnamestd === TopExposureCalc.SHORT;

// tslint:disable-next-line:max-line-length
// example fund: https://en-us.staging.ftsites.com/investments/options/exchange-traded-funds/products/28388/SINGLCLASS/franklin-liberty-systematic-style-premia-etf/FLSP#portfolio
export const isTopLongPosition = (point: PortfolioTopHoldingsDTO): boolean =>
  point.calcnamestd === TopExposureCalc.LONG_POSITION;

export const isTopShortPosition = (point: PortfolioTopHoldingsDTO): boolean =>
  point.calcnamestd === TopExposureCalc.SHORT_POSITION;

export const mapTopExposure = (
  data: PortfolioTopHoldingsDTO[]
): PortfolioAllocation => {
  let componentId: PortfolioComponentId;
  let baseLabelKey: string;
  switch (data[0].calcnamestd) {
    case TopExposureCalc.SHORT:
      componentId = PortfolioComponentId.TOP_SHORT_EXPOSURES;
      baseLabelKey = 'top-short-exposures';
      break;
    case TopExposureCalc.LONG:
      componentId = PortfolioComponentId.TOP_LONG_EXPOSURES;
      baseLabelKey = 'top-long-exposures';
      break;
    case TopExposureCalc.SHORT_POSITION:
      componentId = PortfolioComponentId.TOP_SHORT_POSITIONS;
      baseLabelKey = 'top-short-positions';
      break;
    case TopExposureCalc.LONG_POSITION:
      componentId = PortfolioComponentId.TOP_LONG_POSITIONS;
      baseLabelKey = 'top-long-positions';
      break;
  }
  return {
    componentId,
    baseLabelKey,
    allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
    allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
    asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
    calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
    calculationType: getFirstNonEmptyProp(data, 'calcname'),
    hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
    updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
    data: data.sort(sortPortfolioDataPoints).map(
      (obj: PortfolioTopHoldingsDTO): PortfolioAllocationDataPoint => ({
        label: obj.hldngname,
        breakdown: formatPercentage(obj.brkdwnpct),
        breakdownStd: parseStdNumber(obj.brkdwnpctstd),
      })
    ),
  };
};

export class TopExposureMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: PortfolioTopHoldingsDTO[] = portfolioDto.topholdings;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    const allocations: PortfolioAllocation[] = [];

    const topLongExposureData: PortfolioTopHoldingsDTO[] = data.filter(
      isTopLongExposure
    );
    if (topLongExposureData.length > 0) {
      allocations.push(mapTopExposure(topLongExposureData));
    }

    const topShortExposureData: PortfolioTopHoldingsDTO[] = data.filter(
      isTopShortExposure
    );
    if (topShortExposureData.length > 0) {
      allocations.push(mapTopExposure(topShortExposureData));
    }

    const topLongPositionData: PortfolioTopHoldingsDTO[] = data.filter(
      isTopLongPosition
    );
    if (topLongPositionData.length > 0) {
      allocations.push(mapTopExposure(topLongPositionData));
    }

    const topShortPositionData: PortfolioTopHoldingsDTO[] = data.filter(
      isTopShortPosition
    );
    if (topShortPositionData.length > 0) {
      allocations.push(mapTopExposure(topShortPositionData));
    }

    return allocations;
  }
}
